import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import FormFieldEditor from './FormFieldEditor';

/**
 * PrintPacketModal — THE pre-print screen. Every print button in the app opens this.
 *
 * Shows: which pack is about to print (changeable, with "make default"), the exact
 * forms in order, what auto-fills vs what needs typing, fill-in boxes (keyed per
 * placed field so boxes can never collide), and a Print button.
 *
 * mode 'deal'  : prints a deal packet (Deal Desk, Lite, wizard, edit deal)
 * mode 'titles': prints title forms for one or many vehicles (batch supported)
 */

const SLOT_SHORT = {
  pack_route_retail_in_state: 'in-state retail deals',
  pack_route_retail_out_of_state: 'out-of-state retail deals',
  pack_route_wholesale: 'wholesale deals',
  pack_route_titles: 'title printing',
};

const isFillIn = (f) => f.source === 'manual_entry';
const isAuto = (f) => f.source && f.source !== 'manual_entry' && f.source !== 'static_text' && !String(f.source).startsWith('sign_here');
const isUnnamed = (f) => !f.name || !String(f.name).trim() || f.name === 'manual_entry';
const fieldKey = (templateId, f) => (f.id != null ? `field:${f.id}` : `${templateId}::${f.name || 'manual_entry'}`);

const openPdfDataUrl = (dataUrl) => {
  const base64 = dataUrl.split(',')[1];
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) alert('Allow pop-ups to view the PDF');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const PrintPacketModal = ({ mode = 'deal', deal = null, vehicles = [], onClose }) => {
  const isTitles = mode === 'titles';
  const [packs, setPacks] = useState([]);
  const [packId, setPackId] = useState(null); // number | 'all' (deal mode only)
  const [routedPackId, setRoutedPackId] = useState(null);
  const [routeSlot, setRouteSlot] = useState(null);
  const [forms, setForms] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [manualValues, setManualValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingForms, setLoadingForms] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [editorFieldId, setEditorFieldId] = useState(null);
  const [settingDefault, setSettingDefault] = useState(false);
  // Inline field renaming (name unnamed boxes without leaving this screen)
  const [nameDrafts, setNameDrafts] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [savingNameId, setSavingNameId] = useState(null);

  // Titles batch state
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchPdfs, setBatchPdfs] = useState([]);
  const isBatch = isTitles && vehicles.length > 1;
  const currentVehicle = isTitles ? (vehicles[batchIndex] || vehicles[0]) : null;

  const loadForms = useCallback(async (targetPackId) => {
    setLoadingForms(true);
    try {
      let rows;
      if (isTitles) {
        rows = (await api.getFormPackTemplates(targetPackId) || []).map(t => ({
          id: t.id,
          name: t.name,
          ready: !!(t.blank_file_path || t.blank_file_url),
          fields: t.fields || [],
        }));
      } else if (targetPackId === 'all') {
        rows = await api.getAvailableForms(deal.id, null, true);
      } else {
        rows = await api.getAvailableForms(deal.id, targetPackId);
      }
      setForms(rows || []);
      setCheckedIds(
        targetPackId === 'all' ? [] : (rows || []).filter(f => f.ready !== false).map(f => f.id)
      );
    } catch (err) {
      console.error('Load forms failed:', err);
      setForms([]);
      setCheckedIds([]);
    } finally {
      setLoadingForms(false);
    }
  }, [isTitles, deal?.id]);

  useEffect(() => {
    const init = async () => {
      try {
        if (isTitles) {
          // Default titles pack comes from the What-Prints-Where 'Titles' slot
          // (settable right here via "Make default"); falls back to the first pack.
          const [titlePacks, settings] = await Promise.all([
            api.getFormPacks('titles').then(r => r || []),
            api.getSettings().catch(() => ({})),
          ]);
          setPacks(titlePacks);
          const routedRaw = parseInt(settings?.pack_route_titles, 10);
          const routed = titlePacks.some(p => p.id === routedRaw) ? routedRaw : null;
          setRouteSlot('pack_route_titles');
          setRoutedPackId(routed);
          const first = routed || titlePacks[0]?.id || null;
          setPackId(first);
          if (first) await loadForms(first);
        } else {
          const dealPacks = (await api.getFormPacks() || []).filter(p => (p.pack_type || 'retail') !== 'titles');
          setPacks(dealPacks);
          // Server resolves the routed pack (sale type + buyer state) and tells us the slot
          const rows = await api.getAvailableForms(deal.id);
          const routed = rows?.[0]?.packId || null;
          setRouteSlot(rows?.[0]?.routeSlot || (deal.sale_type === 'retail' ? 'pack_route_retail_in_state' : 'pack_route_wholesale'));
          setRoutedPackId(routed);
          const target = routed || dealPacks[0]?.id || null;
          setPackId(target);
          if (routed) {
            setForms(rows || []);
            setCheckedIds((rows || []).filter(f => f.ready !== false).map(f => f.id));
          } else if (target) {
            await loadForms(target);
          }
        }
      } catch (err) {
        console.error('Print packet init failed:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line
  }, []);

  const handlePackChange = (value) => {
    const next = isTitles || value === 'all' ? (value === 'all' ? 'all' : parseInt(value, 10)) : parseInt(value, 10);
    setPackId(next);
    setManualValues({});
    loadForms(next);
  };

  const handleMakeDefault = async () => {
    if (!routeSlot || packId === 'all' || !packId) return;
    setSettingDefault(true);
    try {
      await api.updateSettings({ [routeSlot]: String(packId) });
      setRoutedPackId(packId);
    } catch (err) {
      alert('Failed to set default: ' + err.message);
    } finally {
      setSettingDefault(false);
    }
  };

  const toggleForm = (id) => {
    setCheckedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const checkedForms = forms.filter(f => checkedIds.includes(f.id));
  // Fill-in boxes grouped by NAME: the same named box on several forms ("BODY STYLE")
  // is one question — type it once, it fills every form. Unnamed boxes stay separate
  // (distinct unknowns until named). Values still go to the server per-field.
  const fillInGroups = (() => {
    const groups = [];
    const byName = new Map();
    checkedForms.forEach(form => {
      (form.fields || []).filter(isFillIn).forEach(field => {
        const f = { ...field, formId: form.id, formName: form.name };
        const nameKey = isUnnamed(f) ? null : String(f.name).trim().toLowerCase();
        if (nameKey && byName.has(nameKey)) {
          byName.get(nameKey).fields.push(f);
          return;
        }
        const group = { key: nameKey ? `name:${nameKey}` : fieldKey(form.id, f), name: f.name, unnamed: !nameKey, fields: [f] };
        groups.push(group);
        if (nameKey) byName.set(nameKey, group);
      });
    });
    return groups;
  })();

  const handlePrint = async () => {
    if (checkedIds.length === 0) { alert('Select at least one form'); return; }
    setGenerating(true);
    try {
      const orderedIds = forms.filter(f => checkedIds.includes(f.id)).map(f => f.id);
      // Fan each group's typed value out to every member box's own per-field key
      const expandedValues = {};
      fillInGroups.forEach(g => {
        const val = manualValues[g.key];
        if (val === undefined || val === '') return;
        g.fields.forEach(f => { expandedValues[fieldKey(f.formId, f)] = val; });
      });
      if (isTitles) {
        const result = await api.generateTitleForms(currentVehicle.id, packId, expandedValues, orderedIds);
        if (isBatch) {
          const updated = [...batchPdfs, result.pdfDataUrl];
          setBatchPdfs(updated);
          if (batchIndex < vehicles.length - 1) {
            setBatchIndex(batchIndex + 1);
            setManualValues({});
            return; // stay open for the next vehicle
          }
          updated.forEach((dataUrl, i) => setTimeout(() => openPdfDataUrl(dataUrl), i * 600));
          onClose();
        } else {
          openPdfDataUrl(result.pdfDataUrl);
          onClose();
        }
      } else {
        const result = await api.generateForms(deal.id, orderedIds, null, expandedValues);
        if (result.pdfDataUrl) {
          openPdfDataUrl(result.pdfDataUrl);
          onClose();
        } else {
          alert('No PDF returned');
        }
      }
    } catch (err) {
      alert('Failed to generate forms: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const openFieldEditor = async (formId, fieldId = null) => {
    try {
      const template = await api.getFormTemplate(formId);
      setEditorFieldId(fieldId);
      setEditorTemplate(template);
    } catch (err) {
      alert('Failed to open editor: ' + err.message);
    }
  };

  // Inline rename: name a fill-in box (or a whole same-named group) right here —
  // no Field Editor round trip. The PUT preserves position/size/format.
  const saveGroupName = async (group) => {
    const draft = (nameDrafts[group.key] || '').trim();
    if (!draft) { alert('Type a name first (e.g. "Purchase Price")'); return; }
    setSavingNameId(group.key);
    try {
      for (const f of group.fields) {
        if (f.id != null) await api.updateFormTemplateField(f.formId, f.id, { fieldName: draft });
      }
      setRenamingId(null);
      setNameDrafts(d => { const n = { ...d }; delete n[group.key]; return n; });
      await loadForms(packId);
    } catch (err) {
      alert('Failed to save name: ' + err.message);
    } finally {
      setSavingNameId(null);
    }
  };

  const selectedPackName = packId === 'all' ? 'All forms' : (packs.find(p => p.id === packId)?.name || '—');
  const titleText = isTitles
    ? (isBatch
        ? `Print Title Forms — Vehicle ${batchIndex + 1} of ${vehicles.length}: ${currentVehicle?.year} ${currentVehicle?.make} ${currentVehicle?.model}`
        : `Print Title Forms — ${currentVehicle?.year} ${currentVehicle?.make} ${currentVehicle?.model}`)
    : `Print Forms — Deal #${deal?.id}${deal?.buyer_name ? ` · ${deal.buyer_name}` : ''}`;

  const packNameLower = (selectedPackName || '').toLowerCase();
  const isRepoPack = isTitles && packNameLower.includes('repo');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3.5 border-b flex items-start justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold">{titleText}</h2>
            <p className="text-xs text-neutral-400">Nothing prints without this screen — what you see is what comes out.</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none px-1">✕</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-neutral-400">Loading…</div>
        ) : (
          <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
            {/* This will print: pack selector */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">This will print:</label>
              <div className="flex items-center gap-2">
                <select
                  value={packId === 'all' ? 'all' : (packId || '')}
                  onChange={e => handlePackChange(e.target.value)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm font-medium bg-white"
                >
                  {packs.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.template_count} forms)</option>
                  ))}
                  {!isTitles && <option value="all">All forms (everything in the system)</option>}
                </select>
              </div>
              {routeSlot && packId !== 'all' && packId && packId !== routedPackId && (
                <button
                  onClick={handleMakeDefault}
                  disabled={settingDefault}
                  className="mt-1.5 text-[11px] text-indigo-600 hover:text-indigo-800 underline disabled:opacity-50"
                >
                  {settingDefault ? 'Saving…' : `Make "${selectedPackName}" the default for ${SLOT_SHORT[routeSlot] || 'these prints'}`}
                </button>
              )}
              {routeSlot && packId === routedPackId && packId && (
                <p className="mt-1 text-[11px] text-neutral-400">Default pack for {SLOT_SHORT[routeSlot] || 'these prints'}.</p>
              )}
            </div>

            {/* Forms list */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">Forms (in print order):</label>
              {loadingForms ? (
                <p className="text-sm text-neutral-400 py-4 text-center">Loading forms…</p>
              ) : forms.length === 0 ? (
                <p className="text-sm text-neutral-400 py-4 text-center border border-dashed rounded-lg">
                  This pack is empty — add forms in the Pack Editor (Form Templates).
                </p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {forms.map(form => {
                    const autoCount = (form.fields || []).filter(isAuto).length;
                    const fillIns = (form.fields || []).filter(isFillIn);
                    return (
                      <div key={form.id} className={`border rounded-lg p-2 ${form.ready === false ? 'opacity-50 bg-neutral-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={checkedIds.includes(form.id)}
                            onChange={() => toggleForm(form.id)}
                            disabled={form.ready === false}
                            className="w-4 h-4"
                          />
                          <span className="flex-1 text-sm font-medium truncate">{form.name}</span>
                          {form.ready === false && (
                            <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded" title="No print-ready file">Needs setup</span>
                          )}
                          <button
                            onClick={() => openFieldEditor(form.id)}
                            className="text-[11px] text-blue-600 hover:text-blue-800 whitespace-nowrap"
                            title="Change what auto-fills and what's typed by hand on this form"
                          >
                            Edit fields
                          </button>
                        </div>
                        <div className="ml-6 mt-0.5 text-[10px] text-neutral-400">
                          {autoCount > 0 && <span>{autoCount} auto-filled</span>}
                          {autoCount > 0 && fillIns.length > 0 && <span> · </span>}
                          {fillIns.length > 0 && <span>{fillIns.length} fill-in box{fillIns.length === 1 ? '' : 'es'} below</span>}
                          {fillIns.length === 0 && (
                            <span>{autoCount > 0 ? ' · ' : ''}no fill-in boxes — Edit fields to add a typeable spot</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fill-in boxes — same-named boxes across forms merge into ONE input;
                the value fans out to each form's own box at print time */}
            {fillInGroups.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Fill in before printing:</label>
                <div className="space-y-2">
                  {fillInGroups.map((g) => {
                    const f0 = g.fields[0];
                    const multi = g.fields.length > 1;
                    const formNames = g.fields.map(f => f.formName).join(', ');
                    const canRename = g.fields.every(f => f.id != null);
                    // Unnamed boxes ALWAYS show the naming bar — that's the whole point
                    const renaming = canRename && (g.unnamed || renamingId === g.key);
                    return (
                      <div key={g.key}>
                        {renaming ? (
                          /* Name the box right here — saves to the form(s), list refreshes */
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <input
                              type="text"
                              value={nameDrafts[g.key] !== undefined ? nameDrafts[g.key] : (g.unnamed ? '' : g.name)}
                              onChange={e => setNameDrafts(d => ({ ...d, [g.key]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveGroupName(g); }}
                              placeholder={`Name this box on ${f0.formName} (e.g. "Purchase Price")`}
                              className="flex-1 border border-red-300 bg-red-50 rounded px-2 py-1 text-[11px] font-medium"
                            />
                            <button
                              onClick={() => saveGroupName(g)}
                              disabled={savingNameId === g.key}
                              className="px-2 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
                            >
                              {savingNameId === g.key ? 'Saving…' : 'Save name'}
                            </button>
                            <button
                              onClick={() => openFieldEditor(f0.formId, f0.id)}
                              tabIndex={-1}
                              title="See exactly where this box sits on the form"
                              className="px-1.5 py-1 text-[11px] text-blue-600 underline whitespace-nowrap"
                            >
                              show me
                            </button>
                          </div>
                        ) : (
                          <label className={`block text-[11px] mb-0.5 ${g.unnamed ? 'text-amber-600' : 'text-neutral-500'}`}>
                            {g.unnamed ? 'Unnamed field' : g.name}
                            {multi ? (
                              <span className="text-teal-600 font-semibold" title={formNames}> — fills {g.fields.length} forms</span>
                            ) : (
                              <span className="text-neutral-400 italic"> ({f0.formName})</span>
                            )}
                            {canRename ? (
                              <button
                                onClick={() => { setRenamingId(g.key); setNameDrafts(d => ({ ...d, [g.key]: g.unnamed ? '' : g.name })); }}
                                tabIndex={-1}
                                title={multi ? `Rename this box on all ${g.fields.length} forms` : 'Rename this box'}
                                className="ml-1.5 text-neutral-400 hover:text-blue-600"
                              >
                                ✎
                              </button>
                            ) : g.unnamed && (
                              <button onClick={() => openFieldEditor(f0.formId)} tabIndex={-1} className="ml-2 text-amber-700 underline">
                                name it in the Field Editor
                              </button>
                            )}
                          </label>
                        )}
                        <input
                          type="text"
                          value={manualValues[g.key] || ''}
                          onChange={e => setManualValues(v => ({ ...v, [g.key]: e.target.value }))}
                          placeholder={g.unnamed ? 'Value for this box' : `Enter ${g.name}${multi ? ' (fills all ' + g.fields.length + ' forms)' : ''}`}
                          className={`w-full border rounded-lg px-3 py-2 text-sm ${g.unnamed ? 'border-amber-300 bg-amber-50' : 'border-neutral-200'}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Titles pre-print checklist (kept from the old titles modal) */}
            {isTitles && packId && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2">Before You Print — Check These:</p>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2 text-xs text-red-800"><span className="mt-0.5 font-bold">•</span><span><strong>Body Style</strong> — make sure it is filled in on the form</span></li>
                  <li className="flex items-start gap-2 text-xs text-red-800"><span className="mt-0.5 font-bold">•</span><span><strong>Seller name &amp; signature</strong> — must be completed before submitting</span></li>
                  {isRepoPack && (
                    <>
                      <li className="flex items-start gap-2 text-xs text-red-800"><span className="mt-0.5 font-bold">•</span><span><strong>Odometer boxes</strong> — check the correct ODO disclosure box on the affidavit</span></li>
                      <li className="flex items-start gap-2 text-xs text-red-800"><span className="mt-0.5 font-bold">•</span><span><strong>Sign &amp; date</strong> — all repo paper signature lines must be signed</span></li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3.5 border-t flex items-center justify-between flex-shrink-0">
          <span className="text-xs text-neutral-400">
            {checkedIds.length} form{checkedIds.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex gap-2">
            {isBatch && batchPdfs.length > 0 && (
              <button
                onClick={() => { batchPdfs.forEach((d, i) => setTimeout(() => openPdfDataUrl(d), i * 600)); onClose(); }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                Open {batchPdfs.length} Ready
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-neutral-500 hover:text-neutral-700 text-sm">Cancel</button>
            <button
              onClick={handlePrint}
              disabled={generating || checkedIds.length === 0 || loading}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {generating
                ? 'Generating…'
                : isBatch && batchIndex < vehicles.length - 1
                  ? `Print & Next (${batchIndex + 1}/${vehicles.length})`
                  : 'Print'}
            </button>
          </div>
        </div>
      </div>

      {/* Inline Field Editor — edit what auto-fills / fill-in names, then the list refreshes */}
      {editorTemplate && (
        <div onClick={e => e.stopPropagation()}>
          <FormFieldEditor
            template={editorTemplate}
            initialFieldId={editorFieldId}
            onClose={() => { setEditorTemplate(null); setEditorFieldId(null); loadForms(packId); }}
            onSave={() => loadForms(packId)}
          />
        </div>
      )}
    </div>
  );
};

export default PrintPacketModal;
